import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import Modal from "./Modal";
import { useNotifications } from "../contexts/NotificationsContext";
import { useAuth } from "../contexts/AuthContext";
import { htmlToPlainText } from "../utils/sanitize";

/**
 * WebNotificationManager
 * - Shows an opt-in modal to enable browser notifications
 * - Listens for new unread notifications (from NotificationsContext)
 *   and triggers Web Notifications for newly-added items only
 * - On click, focuses the app and navigates to the relevant question/comment
 */
export default function WebNotificationManager() {
  const { notifications, loaded } = useNotifications();
  const [showOptIn, setShowOptIn] = useState(false);
  const location = useLocation();
  const { user } = useAuth();

  // Track the set of notification IDs we've already seen to avoid duplicates
  const seenIdsRef = useRef(new Set());
  const initializedRef = useRef(false);

  const isSupported = useMemo(() => typeof window !== "undefined" && "Notification" in window, []);

  // Only show the opt-in for logged-in users OR share-url visitors, and never on auth pages
  const eligibleForModal = useMemo(() => {
    if (!isSupported) return false;
    const pathname = location?.pathname || "";
    if (pathname === "/login" || pathname === "/signup") return false;
    const params = new URLSearchParams(location?.search || "");
    const hasShareToken = !!params.get("share_token");
    return !!user || hasShareToken;
  }, [isSupported, location?.pathname, location?.search, user]);

  // Decide when to show opt-in modal
  useEffect(() => {
    if (!eligibleForModal) {
      setShowOptIn(false);
      return;
    }

    const stored = localStorage.getItem("web_notif_status");
    const permission = window.Notification?.permission;

    // Only show if permission prompt hasn't been decided and user hasn't dismissed before
    if (permission === "default" && stored !== "denied" && stored !== "dismissed") {
      setShowOptIn(true);
    }
  }, [eligibleForModal]);

  const requestPermission = async () => {
    try {
      if (!isSupported) return;
      const result = await window.Notification.requestPermission();
      if (result === "granted") {
        localStorage.setItem("web_notif_status", "granted");
      } else if (result === "denied") {
        localStorage.setItem("web_notif_status", "denied");
      }
    } catch (e) {
      // no-op
    } finally {
      setShowOptIn(false);
    }
  };

  const dismissOptIn = () => {
    // Remember we dismissed to avoid nagging
    localStorage.setItem("web_notif_status", "dismissed");
    setShowOptIn(false);
  };

  // Helper: build URL to navigate to from a notification click.
  const buildTargetUrl = (n) => {
    const data = n?.data || {};
    const projectId = data.project_id;
    if (!projectId) return null;

    // Preserve any existing search params (e.g., share_token, data, etc.)
    const currentSearch = window.location.search || "";
    const params = new URLSearchParams(currentSearch);

    // Indicate scroll target via query params (ProjectDetail will read these)
    if (data.question_id) params.set("scrollToQuestionId", String(data.question_id));
    if (data.comment_id) params.set("scrollToCommentId", String(data.comment_id));

    const searchStr = params.toString();
    return `${window.location.origin}/projects/${projectId}${searchStr ? `?${searchStr}` : ""}`;
  };

  // Show a browser notification for a new unread item
  const showBrowserNotification = (n) => {
    if (!isSupported || window.Notification.permission !== "granted") return;

    const data = n?.data || {};
    const type = n?.type || "notification";

    const title = type === "comment_added"
      ? (data.author_name ? `${data.author_name} commented` : "New comment")
      : type === "question_added"
        ? (data.creator_name ? `${data.creator_name} asked a question` : "New question")
        : "Notification";

    const body = htmlToPlainText(data.content || "").trim() || undefined;
    const targetUrl = buildTargetUrl(n);

    try {
      const notif = new window.Notification(title, {
        body,
        // tag can help coalesce duplicates per-ID
        tag: n.id ? String(n.id) : undefined,
      });

      notif.onclick = () => {
        try { window.focus(); } catch {}
        if (targetUrl) {
          window.location.href = targetUrl; // preserve share params; ProjectDetail handles scrolling
        }
        try { notif.close(); } catch {}
      };
    } catch (e) {
      // Creating notification can throw if permission changed; ignore
    }
  };

  // When unread notifications update, show web notifications only for newly-added ones.
  useEffect(() => {
    if (!loaded) return;

    const currentIds = new Set((notifications || []).map((n) => n.id));

    if (!initializedRef.current) {
      // Initialize without showing notifications for pre-existing unread
      seenIdsRef.current = currentIds;
      initializedRef.current = true;
      return;
    }

    // Determine which notifications are new
    const newOnes = (notifications || []).filter((n) => !seenIdsRef.current.has(n.id));

    // Update seen set
    newOnes.forEach((n) => seenIdsRef.current.add(n.id));

    // Show web notifications for new ones
    newOnes.forEach((n) => showBrowserNotification(n));
  }, [notifications, loaded]);

  if (!eligibleForModal || !showOptIn) return null;

  return (
    <Modal title="Enable Browser Notifications" onClose={dismissOptIn}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <p style={{ margin: 0 }}>
          Stay up to date with new questions and comments. Enable browser notifications to get alerts even when you're in another tab.
        </p>
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <button className="btn" onClick={requestPermission} id="enable-browser-notifs">Enable notifications</button>
          <button className="btn btn-secondary" onClick={dismissOptIn} id="dismiss-browser-notifs">Not now</button>
        </div>
      </div>
    </Modal>
  );
}
